<template>
  <Modal
    v-model:visible="isVisible"
    class="planning-modal"
    :header="t('routeBuilder.routePlanning')"
    no-scroll
    @keypress.enter="onSubmit"
    @keypress.esc="onCancel"
  >
    <template #default>
      <Transition name="step-one-transition">
        <PlanningModalStepOne
          v-if="isSelectingRoutePlanType"
          ref="step-one"
          v-model="routePlanType"
          :ship-orders="shipOrders"
          :available-route-plan-types="AVAILABLE_ROUTE_PLAN_TYPES"
          @option-dblclick="onSubmitAfterDoubleClick"
        />
      </Transition>

      <Transition name="step-two-transition">
        <PlanningModalStepTwo
          v-if="!isSelectingRoutePlanType"
          ref="step-two"
          v-model="crossdocks"
          :number-of-crossdocks="numCrossdocks"
        />
      </Transition>
    </template>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <RygenButton
        v-if="isSelectingRoutePlanType"
        mode="primary"
        :icon="isSingleStepType ? PrimeIcons.CHECK : PrimeIcons.ARROW_RIGHT"
        :label="isSingleStepType ? t('common.submit') : t('common.next')"
        :disabled="!isValid || !routePlanType"
        @click="onSubmit"
      />
      <RygenButton
        v-else
        mode="primary"
        :icon="PrimeIcons.CHECK"
        :label="t('common.submit')"
        :disabled="!isValid"
        @click="onSubmit"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { useFlag } from '@unleash/proxy-client-vue';
import type { PropType, Ref } from 'vue';
import { computed, onMounted, ref, toRefs, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Location, Optional } from '@rygen/rygen-ui/model';

import {
  generateScaffoldPayload,
  NUM_CROSSDOCKS_BY_ROUTE_PLAN_TYPE,
  RoutePlanType,
  ScaffoldRoutePlanPayload,
  ValidShipOrder,
} from '@/model/routeBuilder';

import PlanningModalStepOne from '@/components/routeBuilder/planningPool/PlanningModalStepOne.vue';
import PlanningModalStepTwo from '@/components/routeBuilder/planningPool/PlanningModalStepTwo.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';

const AVAILABLE_ROUTE_PLAN_TYPES: RoutePlanType[] = [RoutePlanType.CONSOLIDATE];

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  shipOrders: {
    type: Array as PropType<ValidShipOrder[]>,
    default: () => [],
  },
});
const { shipOrders } = toRefs(props);
const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'submit', payload: ScaffoldRoutePlanPayload): void;
}>();

const isTransportSegmentEnabled = useFlag(
  'route-builder-transport-segment-feat-flag',
);

const planningModalStepOne = useTemplateRef('step-one');
const planningModalStepTwo = useTemplateRef('step-two');

const { t } = useI18n();

const routePlanType: Ref<Optional<RoutePlanType>> = ref();
const crossdocks: Ref<Location[]> = ref([]);

const isSelectingRoutePlanType = ref(true);
const isValid = ref(true);

const numCrossdocks = computed(() => {
  return routePlanType.value
    ? NUM_CROSSDOCKS_BY_ROUTE_PLAN_TYPE[routePlanType.value]
    : 0;
});

const isSingleStepType = computed(() => numCrossdocks.value === 0);

const validate = async () => {
  const result = isSelectingRoutePlanType.value
    ? await planningModalStepOne.value?.validate()
    : await planningModalStepTwo.value?.validate();

  isValid.value = result ?? false;
  return isValid.value;
};

const onSubmitAfterDoubleClick = (newVal: RoutePlanType) => {
  routePlanType.value = newVal;
  onSubmit();
};

const onSubmit = async () => {
  if (!(await validate())) {
    return;
  }
  // Validation ensures `routePlanType` is not `undefined`
  const type = routePlanType.value as RoutePlanType;

  if (!isSingleStepType.value && isSelectingRoutePlanType.value) {
    isSelectingRoutePlanType.value = false;
    return;
  }

  const payload = generateScaffoldPayload(
    type,
    shipOrders.value,
    crossdocks.value,
  );
  isVisible.value = false;
  emit('submit', payload);
};

const onCancel = () => {
  isVisible.value = false;
  emit('cancel');
};

watch(isVisible, (newVal) => {
  if (newVal) {
    isSelectingRoutePlanType.value = true;
    routePlanType.value = undefined;
    crossdocks.value = [];
    isValid.value = true;
  }
});

onMounted(() => {
  if (isTransportSegmentEnabled.value) {
    AVAILABLE_ROUTE_PLAN_TYPES.push(RoutePlanType.POOL_CONSOLIDATE);
    AVAILABLE_ROUTE_PLAN_TYPES.push(RoutePlanType.POOL_DISTRIBUTION);
  }
});
</script>

<style>
.planning-modal {
  .modal-content {
    min-height: 98px;

    .step-one-transition-leave-active {
      animation: slide-out 0.5s ease-out forwards;
    }

    .step-two-transition-enter-active {
      animation: slide-in 0.8s ease backwards;
    }
  }
}

@keyframes slide-out {
  100% {
    opacity: 0;
    transform: translateX(-500px);
    position: absolute;
    top: -100vh;
  }
}

@keyframes slide-in {
  0% {
    position: absolute;
    top: 50%;
    transform: translateX(400px);
    opacity: 0;
  }

  100% {
    transform: none;
    opacity: 1;
  }
}
</style>
